import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Footer from "./common/Footer";
import Header from "./common/Header";
import Preloader from '../components/common/Preloader';
import SlidersHotels from "./sliders/hotels/SlidersHotels";
import Sliders_2_hotels from "./sliders/hotels/Sliders_2_Hotels";
import FrontendPageSEO from '../components/common/FrontendPageSEO';
import Sliders1_Hotels from "./sliders/hotels/Sliders1_Hotels";
import LoginPopup from './popup/LoginPopup';
import axios from 'axios';
import useFrontendPageContent from '../components/common/useFrontendPageContent';
import DynamicSectionTitle from '../components/common/DynamicSectionTitle';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const styles = `
.search-container { position: relative; width: 100%; }
.suggestions-dropdown { position: absolute; top: 100%; left: 0; right: 0; background-color: white; border: 1px solid #ddd; border-top: none; border-radius: 0 0 8px 8px; z-index: 9999 !important; max-height: 300px; overflow-y: auto; text-align: left; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
.suggestion-item { padding: 12px 20px; cursor: pointer; border-bottom: 1px solid #eee; }
.suggestion-item:last-child { border-bottom: none; }
.suggestion-item:hover { background-color: #f5f5f5; }
.suggestion-item strong { color: #333; }
.suggestion-item small { color: #888; margin-left: 8px; text-transform: capitalize; }
`;

function Hotels() {
  const location = useLocation();
  const navigate = useNavigate();
  const { pageContent, getSection, isSectionActive, loading } = useFrontendPageContent('hotels');
  const [isLoginPopupOpen, setIsLoginPopupOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);

    const themeStylesheet = document.getElementById("hotelpopup-css");
    if (location.pathname === "/hotels") {
      if (!themeStylesheet) {
        const link = document.createElement("link");
        link.id = "hotelpopup-css";
        link.rel = "stylesheet";
        link.href = "assets/css/LoginPopup.css";
        document.head.appendChild(link);
      }
    } else {
      if (themeStylesheet) {
        document.head.removeChild(themeStylesheet);
      }
    }

    return () => {
      if (styleSheet.parentNode) {
        document.head.removeChild(styleSheet);
      }
    };
  }, [location]);

  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    const debounceTimer = setTimeout(() => {
      const fetchSuggestions = async () => {
        setIsSuggestionsLoading(true);
        try {
          const response = await axios.get(`${API_BASE_URL}/hotels/search?q=${searchQuery}`);
          if (response.data && Array.isArray(response.data)) {
            setSuggestions(response.data);
          } else {
            setSuggestions([]);
          }
        } catch (error) {
          console.error("Failed to fetch suggestions:", error);
          setSuggestions([]);
        } finally {
          setIsSuggestionsLoading(false);
        }
      };
      fetchSuggestions();
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/hotel-list?city=${searchQuery.trim()}`);
      setSuggestions([]);
    }
  };

  const slugify = (str) =>
    str ? String(str).toLowerCase().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '').replace(/\-\-+/g, '-').replace(/^-+/, '').replace(/-+$/, '') : '';

  const generateHotelSlug = (hotel) => {
    const name = slugify(hotel?.name || hotel?.hotel_name);
    const city = slugify(hotel?.city);
    const price = Math.round(hotel?.offer_price || hotel?.hotel_price || hotel?.price || 0);
    const id = hotel?.id;
    return [name, city, price ? price : null, id].filter(Boolean).join('-').replace(/--+/g, '-');
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.name);
    setSuggestions([]);
    if (suggestion.type === 'city') {
      navigate(`/hotel-list?city=${suggestion.name}`);
    } else if (suggestion.type === 'hotel') {
      const slug = generateHotelSlug(suggestion);
      window.open(`/hotel-details/${slug}`, '_blank', 'noopener,noreferrer');
    }
  };

  const destinationsSection = getSection('popular_destinations', {
    Items: [
      { id: 1, title: "Delhi", image_url: "assets/img/hotels/hotels-places/del-sm.webp" },
      { id: 2, title: "Goa", image_url: "assets/img/hotels/hotels-places/goa-sm.webp" },
      { id: 3, title: "Mumbai", image_url: "assets/img/hotels/hotels-places/mumb-sm.webp" },
      { id: 4, title: "Shimla", image_url: "assets/img/hotels/hotels-places/shimla-sm.webp" },
      { id: 5, title: "Manali", image_url: "assets/img/hotels/hotels-places/manali-sm.webp" },
      { id: 6, title: "Hyderabad", image_url: "assets/img/hotels/hotels-places/hyd-sm.webp" },
      { id: 7, title: "Chennai", image_url: "assets/img/hotels/hotels-places/chennai-sm.webp" },
      { id: 8, title: "Kolkata", image_url: "assets/img/hotels/hotels-places/kolkata-sm.webp" },
      { id: 9, title: "Jaipur", image_url: "assets/img/hotels/hotels-places/jaipur-sm.webp" },
      { id: 10, title: "Pune", image_url: "assets/img/hotels/hotels-places/pune-sm.webp" },
    ],
  });
  const cityDestinationsSection = getSection('city_destinations', {
    title: 'at Popular Destinations',
    title_highlight: 'Book Hotels',
    Items: destinationsSection.Items
  });
  const whyBookSection = getSection('why_book_hotels', {
    title: 'With mangoloops?',
    title_highlight: 'Why Book Hotels',
  });
  const exclusiveDealsSection = getSection('exclusive_deals', {
    title: 'Deals and Savings',
    title_highlight: 'Unlock Exclusive',
  });

  const allHotels = (cityDestinationsSection.Items || []).map((item, index) => ({
    
    id: item.id || index + 1,
    name: item.title || item.name,
    imgSrc: item.image_url || item.imgSrc,
    description: item.description || '',
  }));

  const [visibleHotelCount, setVisibleHotelCount] = useState(9);

  const loadMore = () => {
    setVisibleHotelCount((current) => current + 6);
  };

  if (loading || !pageContent) {
    return <Preloader />;
  }

  return (
    <div>
      <FrontendPageSEO
        page={pageContent}
        fallbackTitle="Book Best Hotels & Stays | Mangoloops"
        fallbackDescription="Find and book the best hotels across top destinations with Mangoloops. Enjoy a comfortable stay with our handpicked selection of hotels and resorts."
        fallbackSlug="/hotels"
      />
      <Header onLoginClick={() => setIsLoginPopupOpen(true)} />
      <LoginPopup isOpen={isLoginPopupOpen} onClose={() => setIsLoginPopupOpen(false)} />

      <div className="banner-content tour-search-sec text-center mb-5 mb-lg-8">
        <div className="container-fluid">
          <div className="row justify-content-center py-xl-4">
            <div className="col-lg-8">
              <div className="card-body">
                <form className="form-container search-input-form" onSubmit={handleSearchSubmit}>
                  <div className="search-container">
                    <span><i className="fa fa-search search-icon"></i></span>
                    <input
                      type="text"
                      placeholder="Enter City, Hotel, or Address"
                      className="search-input"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      autoComplete="off"
                    />
                    
                    <button type="submit" className="search-button">Search</button>
                    {isSuggestionsLoading && (
                      <div className="suggestions-dropdown"><div className="suggestion-item">Loading...</div></div>
                    )}
                    {!isSuggestionsLoading && suggestions.length > 0 && (
                      <div className="suggestions-dropdown">
                        {suggestions.map((item, index) => (
                          <div
                            key={`${item.type}-${item.id || index}`}
                            className="suggestion-item"
                            onMouseDown={() => handleSuggestionClick(item)}
                          >
                            <strong>{item.name}</strong>
                            {item.type === 'hotel' && item.city && (
                              <small>Hotel - {item.city}, {item.country}</small>
                            )}
                            {item.type === 'city' && <small>City</small>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isSectionActive('exclusive_deals') && <SlidersHotels section={exclusiveDealsSection} />}
      {isSectionActive('popular_destinations') && <Sliders1_Hotels section={destinationsSection} />}

      {isSectionActive('city_destinations') && (
        <section className="container offers pb-5 hotels-margin-padding_1 ">
          <div className="popular-state pb-0 pt-0">
            <div className="title">
              <DynamicSectionTitle section={cityDestinationsSection} className="pb-2" />
            </div>
          </div>

          <div className="_polrdestnbx mt20">
            {allHotels.slice(0, visibleHotelCount).map((hotel) => (
              <div className="ecpl_rbx" key={hotel.id}>
                <div className="mgdestn">
                  <a onClick={() => navigate(`/hotel-list?city=${hotel.name}`)} style={{ cursor: 'pointer' }}>
                    <img src={hotel.imgSrc} alt={hotel.name} />
                  </a>
                </div>
                <div className="_pckgdscrptn">
                  <h5 className="htlnking hotel_div">
                    <a onClick={() => navigate(`/hotel-list?city=${hotel.name}`)} style={{ cursor: 'pointer' }}>
                      {hotel.name}
                    </a>
                  </h5>
                  <div className="htlnking" style={{ color: '#000' }}>
                    <a onClick={() => navigate(`/hotel-list?city=${hotel.name}`)} style={{ cursor: 'pointer' }}>
                      {hotel.description}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {visibleHotelCount < allHotels.length && (
            <div className="text-center mt-4">
              <button className="btn btn-primary" onClick={loadMore} style={{ cursor: 'pointer' }}>
                Load More
              </button>
            </div>
          )}
        </section>
      )}

      {isSectionActive('why_book_hotels') && <Sliders_2_hotels section={whyBookSection} />}
      <Footer />
    </div>
  );
}

export default Hotels;
